import { useMemo, useState } from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { DeleteEmailModal } from './DeleteEmailModal';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { FLOW_TRIGGER, stanAnalytics } from '@/lib';
import { useUser } from '@/queries';
import { useModal } from '@/hooks/useModal';
import { useStanRouter } from '@/hooks/useStanRouter';
import IcMoreVertical from '@/assets/icons/ic-more-vertical.svg';
import { useEmailEditorStore } from '../stores';

interface EmailActionsMenuProps {
    emailId: number;
    flowId: number;
}

export const EmailActionsMenu = ({ emailId, flowId }: EmailActionsMenuProps) => {
    const trigger = useEmailEditorStore(state => state.trigger);
    const hasSentEmails = useEmailEditorStore(state => state.hasSentEmails);
    const { user } = useUser();
    const { push } = useStanRouter();
    const [selectedValue, setSelectedValue] = useState<DropdownValue | null>(null);
    const deleteModal = useModal();

    const menuOptions: DropdownMenuOption[] = useMemo(() => {
        const options: DropdownMenuOption[] = [];

        // For time-based triggers with sent emails, don't show delete option
        if (!(trigger === FLOW_TRIGGER.TIME && hasSentEmails)) {
            options.push({
                label: 'Delete',
                value: 'delete',
            });
        }

        // Show view recipients if email has been sent
        if (hasSentEmails) {
            options.push({
                label: 'View recipients',
                value: 'view-recipients',
            });
        }

        return options;
    }, [trigger, hasSentEmails]);

    const handleSelect = (value: DropdownValue) => {
        setSelectedValue(value);

        if (value.value === 'delete') {
            stanAnalytics('flow-email-delete-clicked', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { flow_id: flowId, flow_email_id: emailId },
            });
            deleteModal.open();
        } else if (value.value === 'view-recipients') {
            stanAnalytics('flow-email-view-recipients-clicked', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { flow_id: flowId, flow_email_id: emailId },
            });
            push(`/flows/recipients/${emailId}`);
        }

        // Reset selection after action
        setTimeout(() => setSelectedValue(null), 100);
    };

    return (
        <>
            <div className='absolute top-2 right-2 md:top-3 md:right-3'>
                <DropdownProvider
                    initialValue={selectedValue}
                    dropdownId='email-actions-menu'
                    dropdownName='emailActions'
                >
                    <StanDropdownMenu
                        id='email-actions-menu'
                        name='emailActions'
                        options={menuOptions}
                        currentValue={selectedValue}
                        onSelect={handleSelect}
                        className='group'
                    >
                        <IcMoreVertical className='size-6 origin-top transition-transform group-hover:scale-110 xl:size-7' />
                    </StanDropdownMenu>
                </DropdownProvider>
            </div>

            <DeleteEmailModal
                isOpen={deleteModal.isOpen}
                onClose={deleteModal.close}
                flowId={flowId}
                emailId={emailId}
            />
        </>
    );
};
